import { Link } from "@tanstack/react-router";
import { RefreshCw, TriangleAlert, Wifi, WifiOff } from "lucide-react";
import { type ReactElement, useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { useEstadoConexion } from "@/lib/online-status";
import { countConflicts, countUnsynced, subscribeOutbox } from "@/lib/outbox";

// Clase base de la píldora del indicador; el color lo aporta cada estado.
const PILL = "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 font-medium text-xs";

/**
 * useOutboxCounts mantiene dos cuentas de la outbox, recalculadas ante cada cambio: `unsynced` es
 * todo lo no confirmado (pendientes + conflictos), y `conflicts` solo lo que REQUIERE ATENCIÓN
 * (conflict/rejected/blocked). Distinguirlas permite separar «sincronizando» de «hay conflictos».
 */
function useOutboxCounts(): { unsynced: number; conflicts: number } {
  const [counts, setCounts] = useState({ unsynced: 0, conflicts: 0 });
  useEffect(() => {
    let active = true;
    const refresh = (): void => {
      void Promise.all([countUnsynced(), countConflicts()]).then(([unsynced, conflicts]) => {
        if (active) {
          setCounts({ unsynced, conflicts });
        }
      });
    };
    refresh();
    const unsubscribe = subscribeOutbox(refresh);
    return () => {
      active = false;
      unsubscribe();
    };
  }, []);
  return counts;
}

/**
 * SyncIndicator es el estado de conectividad/sincronización de la cabecera. Distingue cinco
 * situaciones: conectividad sin confirmar (aún se está comprobando; #237), sin conexión (el
 * trabajo se encola), conflictos por resolver (rojo, exige atención del técnico), sincronizando
 * (hay pendientes en vuelo) y en línea al día. Cuando hay algo sin confirmar, la píldora enlaza al
 * centro de sincronización (#53). No dispara sincronización: la gobiernan el monitor de
 * conectividad y el runner.
 */
type SyncIndicatorProps = {
  /** Oculta el texto solo en el rail estrecho de escritorio; conserva el nombre accesible. */
  compactoEnEscritorio?: boolean;
};

export function SyncIndicator({ compactoEnEscritorio = false }: SyncIndicatorProps) {
  const estadoConexion = useEstadoConexion();
  const { unsynced, conflicts } = useOutboxCounts();

  let clase: string;
  let icono: ReactElement;
  let texto: string;
  if (estadoConexion === "sin_confirmar") {
    // Antes del primer heartbeat no se afirma nada: ni «En línea» (mentiría) ni «Sin conexión»
    // (alarmaría sin motivo). Neutro y en movimiento: se está comprobando.
    clase = "border-slate-200 bg-slate-50 text-slate-500";
    icono = <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />;
    texto = `Comprobando…${unsynced > 0 ? ` · ${unsynced}` : ""}`;
  } else if (estadoConexion === "offline") {
    clase = "border-amber-200 bg-amber-50 text-amber-700";
    icono = <WifiOff className="h-4 w-4" aria-hidden="true" />;
    texto = `Sin conexión${unsynced > 0 ? ` · ${unsynced}` : ""}`;
  } else if (conflicts > 0) {
    clase = "border-red-200 bg-red-50 text-red-700";
    icono = <TriangleAlert className="h-4 w-4" aria-hidden="true" />;
    texto = `${conflicts} por resolver`;
  } else if (unsynced > 0) {
    clase = "border-primary-200 bg-primary-50 text-primary-700";
    icono = <RefreshCw className="h-4 w-4 animate-spin" aria-hidden="true" />;
    texto = `Sincronizando · ${unsynced}`;
  } else {
    clase = "border-slate-200 bg-slate-50 text-slate-500";
    icono = <Wifi className="h-4 w-4" aria-hidden="true" />;
    texto = "En línea";
  }

  const pill = (
    <span className={cn(PILL, clase)}>
      {icono}
      <span className={cn("hidden sm:inline", compactoEnEscritorio && "md:hidden")}>{texto}</span>
    </span>
  );

  // Con trabajo sin confirmar, la píldora lleva al centro de sincronización; al día, es informativa.
  if (unsynced > 0) {
    return (
      <Link to="/sincronizacion" aria-label={`${texto}. Abrir el centro de sincronización`}>
        {pill}
      </Link>
    );
  }
  return (
    <span role="status" aria-label={texto}>
      {pill}
    </span>
  );
}
